"use client";

import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";

const DEFAULT_FEATURES = [
	{ titleKey: "certificationsTitle", descKey: "certificationsDesc" },
	{ titleKey: "expertiseTitle", descKey: "expertiseDesc" },
	{ titleKey: "transparentProcessTitle", descKey: "transparentProcessDesc" },
] as const;

const DEFAULT_QUALITY_IMAGES = [
	"/image/new-version/machining/five-axis/quality/precision-cnc-machining-guaranteed-quality-01.webp",
	"/image/new-version/machining/five-axis/quality/precision-cnc-machining-guaranteed-quality-02.webp",
	"/image/new-version/machining/five-axis/quality/precision-cnc-machining-guaranteed-quality-03.jpg",
	"/image/new-version/machining/five-axis/quality/precision-cnc-machining-guaranteed-quality-04.jpg",
];

export const MILLING_QUALITY_IMAGES = [
	"/image/new-version/machining/cnc-milling/quality/precision-cnc-machining-guaranteed-quality-01.webp",
	"/image/new-version/machining/cnc-milling/quality/precision-cnc-machining-guaranteed-quality-02.webp",
	"/image/new-version/machining/cnc-milling/quality/precision-cnc-machining-guaranteed-quality-03.jpg",
	"/image/new-version/machining/cnc-milling/quality/precision-cnc-machining-guaranteed-quality-04.webp",
];

export interface QualityFeature {
	title: string;
	description: string;
}

export interface QualityCustomContent {
	title: string;
	description: string;
	features: QualityFeature[];
	imageAlt?: string;
}

export interface QualityProps {
	translationNamespace?: string;
	customContent?: QualityCustomContent;
	customImages?: string[];
	showButton?: boolean;
	buttonText?: string;
	buttonHref?: string;
	titleOnTop?: boolean;
	bgColor?: string;
	featureTitleColor?: "green" | "black";
}

export default function Quality({
	translationNamespace = "fiveAxisQuality",
	customContent,
	customImages,
	showButton = true,
	buttonText,
	buttonHref = "/quality-assurance",
	titleOnTop = false,
	bgColor = "#fcfff4",
	featureTitleColor = "green",
}: QualityProps) {
	const t = useTranslations(translationNamespace);

	const useCustomContent = !!customContent;
	const images = customImages || MILLING_QUALITY_IMAGES;
	const imageAlt = customContent?.imageAlt || t("imageAlt");
	const featureTitleClass = featureTitleColor === "black" ? "text-black" : "text-themePrimary400";
	const title = useCustomContent ? customContent.title : t("title");
	const description = useCustomContent ? customContent.description : t("description");
	const sectionBgColor = bgColor ?? "#fcfff4";

	return (
		<section className="w-full py-8 sm:py-10 md:py-12 lg:py-16" style={{ backgroundColor: sectionBgColor }}>
			<div className="container">
				{titleOnTop && (
					<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{title}
						</h2>
						<p className="text-sm leading-relaxed text-black md:text-base">{description}</p>
					</div>
				)}

				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-12 xl:gap-16">
					<div className="w-full lg:w-[45%]">
						<div className="grid h-full grid-cols-2 gap-3 md:gap-4">
							{images.map((src, index) => (
								<div key={index} className="relative w-full overflow-hidden">
									<Image
										src={src}
										alt={`${imageAlt} ${index + 1}`}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 50vw, 22vw"
									/>
								</div>
							))}
						</div>
					</div>

					<div className="flex w-full flex-col lg:w-[55%]">
						{!titleOnTop && (
							<>
								<h2 className="mb-3 text-2xl font-bold leading-tight text-[#67A526] sm:mb-4 sm:text-[30px] md:text-4xl">
									{title}
								</h2>
								<p className="mb-6 text-sm leading-relaxed text-black md:text-base">{description}</p>
							</>
						)}

						<div
							className={`flex flex-1 flex-col justify-between gap-6 md:gap-12 ${showButton ? "mb-6" : ""}`}
						>
							{useCustomContent
								? customContent.features.map((feature, index) => (
										<div key={index}>
											<h3 className={`mb-1.5 text-base font-semibold sm:text-lg ${featureTitleClass}`}>
												{feature.title}
											</h3>
											<p className="text-sm leading-relaxed text-black md:text-base">{feature.description}</p>
										</div>
									))
								: DEFAULT_FEATURES.map((feature, index) => (
										<div key={index}>
											<h3 className={`mb-1.5 text-base font-semibold sm:text-lg ${featureTitleClass}`}>
												{t(feature.titleKey)}
											</h3>
											<p className="text-sm leading-relaxed text-black md:text-base">{t(feature.descKey)}</p>
										</div>
									))}
						</div>

						{showButton &&
							(buttonHref.startsWith("http") ? (
								<a
									href={buttonHref}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex w-fit items-center justify-center gap-1 text-sm font-bold text-themePrimary400 transition-colors duration-200 hover:text-mainColor hover:underline sm:text-base"
								>
									{buttonText || t("learnMore")}
									<svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M7 17L17 7M17 7H7M17 7v10"
										/>
									</svg>
								</a>
							) : (
								<Link
									href={buttonHref}
									className="inline-flex w-fit items-center justify-center gap-1 text-sm font-bold text-themePrimary400 transition-colors duration-200 hover:text-mainColor hover:underline sm:text-base"
								>
									{buttonText || t("learnMore")}
									<svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M7 17L17 7M17 7H7M17 7v10"
										/>
									</svg>
								</Link>
							))}
					</div>
				</div>
			</div>
		</section>
	);
}
